import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authService } from '../services';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await authService.requestPasswordReset(email);
      setSubmitted(true);
      
      // In development, show the reset link
      if (response.reset_link) {
        setResetLink(response.reset_link);
      }
      
      toast.success('Wenn ein Konto existiert, wurde eine E-Mail gesendet.');
    } catch (err) {
      toast.error('Fehler beim Anfordern des Passwort-Resets');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="forgot-password-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-4"
        >
          <Card className="bg-white shadow-xl border-slate-200">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-2xl">E-Mail gesendet</CardTitle>
              <CardDescription>
                Wenn ein Konto mit der E-Mail <strong>{email}</strong> existiert, 
                haben wir Ihnen einen Link zum Zurücksetzen des Passworts gesendet.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Development: Show reset link */}
              {resetLink && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription className="text-sm">
                    <strong>Entwicklungsmodus:</strong><br />
                    <Link 
                      to={resetLink} 
                      className="text-blue-600 hover:underline break-all"
                    >
                      Hier klicken zum Zurücksetzen
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zur Anmeldung
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="forgot-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-4"
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: 'var(--color-text-primary)' }}>
            Passwort vergessen?
          </h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Geben Sie Ihre E-Mail ein, um einen Reset-Link zu erhalten
          </p>
        </div>

        <Card className="bg-white shadow-xl border-slate-200">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Passwort zurücksetzen
            </CardTitle>
            <CardDescription>
              Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ihre@email.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="reset-email-input"
                  className="border-slate-200"
                />
              </div>

              <Button
                type="submit"
                style={{
                  backgroundColor: 'var(--color-primary)',
                  color: 'var(--color-text-on-primary)'
                }}
                className="w-full hover:opacity-90"
                disabled={loading}
                data-testid="reset-submit-button"
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Wird gesendet...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Reset-Link senden
                  </div>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Link 
                to="/login" 
                className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück zur Anmeldung
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};
